import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  listSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  runScheduleNow,
  listRegions,
} from "../lib/api";
import { Link } from "react-router-dom";
import { Clock, Play, Trash2, Power, Plus } from "lucide-react";

const FREQUENCY_OPTIONS = [
  { id: "daily", label: "Daily" },
  { id: "every_3_days", label: "Every 3 days" },
  { id: "weekly", label: "Weekly (Mon)" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

function frequencyLabel(f) {
  return FREQUENCY_OPTIONS.find((o) => o.id === f)?.label || f;
}

export default function SchedulesPage() {
  const [schedules, setSchedules] = useState(null);
  const [regions, setRegions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [niche, setNiche] = useState("home_decor");
  const [region, setRegion] = useState("CA-ON");
  const [frequency, setFrequency] = useState("weekly");
  const [hour, setHour] = useState(9);
  const [creating, setCreating] = useState(false);

  const load = () => listSchedules().then(setSchedules).catch(() => setSchedules([]));

  useEffect(() => {
    load();
    listRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createSchedule({ niche, region, frequency, hour: Number(hour), enabled: true });
      toast.success("Schedule created");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error("Could not create schedule");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (s) => {
    try {
      await updateSchedule(s.id, { enabled: !s.enabled });
      toast.success(`Schedule ${!s.enabled ? "enabled" : "paused"}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleRunNow = async (s) => {
    try {
      const job = await runScheduleNow(s.id);
      toast.success("Triggered manual run");
      window.location.href = `/admin/research/${job.id}`;
    } catch {
      toast.error("Could not start run");
    }
  };

  const handleDelete = async (s) => {
    if (!confirm("Delete this schedule?")) return;
    try {
      await deleteSchedule(s.id);
      toast.success("Schedule deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="schedules-page">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="micro-label mb-3">AUTOMATION</div>
          <h1 className="text-5xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Schedules
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">
            Automate research runs on a recurring cron. Hours are UTC — a 9 UTC daily run equals
            4-5 AM Ontario time, delivering fresh winners before you start your day.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="new-schedule-btn"
          className="btn-klein text-xs inline-flex items-center gap-2"
        >
          <Plus size={14} strokeWidth={3} /> NEW SCHEDULE
        </button>
      </div>

      {showForm && (
        <div className="border border-zinc-900 p-6 md:p-8 mb-10" data-testid="schedule-form">
          <div className="grid md:grid-cols-5 gap-6 mb-6">
            <div>
              <label className="micro-label block mb-2">Niche</label>
              <select
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                data-testid="sched-niche-select"
                className="w-full px-3 py-2.5 border border-zinc-300 font-mono text-sm bg-white"
              >
                <option value="home_decor">Home Decor</option>
                <option value="gadgets_tech">Gadgets / Tech</option>
              </select>
            </div>
            <div>
              <label className="micro-label block mb-2">Region</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                data-testid="sched-region-select"
                className="w-full px-3 py-2.5 border border-zinc-300 font-mono text-sm bg-white"
              >
                {regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.code} · {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="micro-label block mb-2">Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                data-testid="sched-freq-select"
                className="w-full px-3 py-2.5 border border-zinc-300 font-mono text-sm bg-white"
              >
                {FREQUENCY_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="micro-label block mb-2">Hour (UTC)</label>
              <select
                value={hour}
                onChange={(e) => setHour(e.target.value)}
                data-testid="sched-hour-select"
                className="w-full px-3 py-2.5 border border-zinc-300 font-mono text-sm bg-white"
              >
                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, "0")}:00
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleCreate}
                disabled={creating}
                data-testid="sched-submit-btn"
                className="btn-klein text-xs w-full"
              >
                {creating ? "CREATING..." : "CREATE"}
              </button>
            </div>
          </div>
        </div>
      )}

      {schedules === null && <div className="micro-label">LOADING...</div>}
      {schedules?.length === 0 && !showForm && (
        <div className="border border-zinc-200 p-16 text-center">
          <Clock size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={1.5} />
          <p className="text-zinc-600 mb-6">No schedules yet.</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-klein inline-block text-xs"
          >
            CREATE FIRST SCHEDULE
          </button>
        </div>
      )}

      {schedules?.length > 0 && (
        <div className="border border-zinc-200" data-testid="schedules-list">
          {schedules.map((s, idx) => (
            <div
              key={s.id}
              data-testid={`schedule-item-${idx}`}
              className={`grid md:grid-cols-12 gap-4 p-5 md:p-6 items-center ${
                idx < schedules.length - 1 ? "border-b border-zinc-200" : ""
              } ${s.enabled ? "" : "bg-zinc-50 opacity-70"}`}
            >
              <div className="md:col-span-3">
                <div className="micro-label mb-1">Niche · Region</div>
                <div className="font-black text-base" style={{ fontFamily: "Outfit" }}>
                  {s.niche === "home_decor" ? "Home Decor" : "Gadgets / Tech"}
                </div>
                <div className="text-xs text-zinc-500 font-mono">{s.region || "CA-ON"}</div>
              </div>
              <div className="md:col-span-3">
                <div className="micro-label mb-1">Cadence</div>
                <div className="font-bold">{frequencyLabel(s.frequency)}</div>
                <div className="text-xs text-zinc-500 font-mono">
                  {String(s.hour).padStart(2, "0")}:00 UTC
                </div>
              </div>
              <div className="md:col-span-3">
                <div className="micro-label mb-1">Last Run</div>
                <div className="text-sm font-mono">
                  {s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "—"}
                </div>
              </div>
              <div className="md:col-span-3 flex items-center justify-end gap-2 flex-wrap">
                <button
                  onClick={() => handleRunNow(s)}
                  data-testid={`run-now-${idx}`}
                  className="w-10 h-10 border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white flex items-center justify-center transition"
                  title="Run now"
                >
                  <Play size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => handleToggle(s)}
                  data-testid={`toggle-${idx}`}
                  className={`w-10 h-10 border flex items-center justify-center transition ${
                    s.enabled
                      ? "border-zinc-900 bg-white hover:bg-zinc-100"
                      : "border-zinc-400 text-zinc-400 hover:border-zinc-900 hover:text-zinc-900"
                  }`}
                  title={s.enabled ? "Pause" : "Enable"}
                >
                  <Power size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => handleDelete(s)}
                  data-testid={`delete-schedule-${idx}`}
                  className="w-10 h-10 border border-zinc-900 bg-white hover:bg-red-500 hover:text-white hover:border-red-500 flex items-center justify-center transition"
                  title="Delete"
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 text-center">
        <Link to="/admin/history" className="micro-label text-zinc-500 hover:text-zinc-900">
          VIEW RESEARCH HISTORY &rarr;
        </Link>
      </div>
    </div>
  );
}
